"use client";

import type React from "react";
import { useState, useEffect } from "react";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import { authClient } from "@/lib/authClient";
import { isValidEthiopianPhone, normalizeEthiopianPhone } from "@/lib/phone";
import { ModeToggle } from "@/components/theme-toggle";
import { LanguageSwitcher } from "@/components/language-switcher";
import { toast } from "sonner";
import { Loader2, Sparkles, ShieldCheck } from "lucide-react";

export default function LoginPage() {
  const t = useTranslations("Login");
  const router = useRouter();
  const { data: session, isPending: isSessionPending } = authClient.useSession();
  const [phoneNumber, setPhoneNumber] = useState("");
  const [code, setCode] = useState("");
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [waitTime, setWaitTime] = useState(0);

  useEffect(() => {
    if (session && !isSessionPending) {
      router.replace("/dashboard");
    }
  }, [session, isSessionPending, router]);

  useEffect(() => {
    if (waitTime > 0) {
      const timer = setTimeout(() => setWaitTime(waitTime - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [waitTime]);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, "");
    if (value.startsWith("251")) {
      value = value.slice(3);
    }
    if (value.length > 0 && !value.startsWith("0") && !value.startsWith("9")) {
      value = "9" + value;
    }
    if (value.length > 10) {
      value = value.slice(0, 10);
    }
    setPhoneNumber(value);
  };

  const handleGetCode = async () => {
    if (!isValidEthiopianPhone(phoneNumber)) {
      toast.error(t("errors.invalidPhone"));
      return;
    }

    setIsLoading(true);
    try {
      const normalizedPhone = normalizeEthiopianPhone(phoneNumber);
      const { data, error } = await authClient.phoneNumber.sendOtp({
        phoneNumber: normalizedPhone,
      });

      if (error) {
        toast.error(error.message || t("errors.sendOtpFailed"));
        console.error("Send OTP error:", error);
      } else {
        setIsCodeSent(true);
        setWaitTime(60);
        toast.success("Verification code sent!");
      }
    } catch (error: any) {
      toast.error(error?.message || t("errors.sendOtpFailed"));
      console.error("Send OTP error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = async () => {
    if (!code || code.length < 6) {
      toast.error(t("errors.enterCode"));
      return;
    }

    setIsLoading(true);
    try {
      const normalizedPhone = normalizeEthiopianPhone(phoneNumber);
      const { data, error } = await authClient.phoneNumber.verify({
        phoneNumber: normalizedPhone,
        code: code,
      });

      if (error) {
        toast.error(error.message || t("errors.verifyFailed"));
        console.error("Verify error:", error);
      } else {
        toast.success("Login successful!");
        window.location.href = "/dashboard";
      }
    } catch (error: any) {
      toast.error(error?.message || t("errors.verifyFailed"));
      console.error("Verify error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/10 via-background to-secondary/10 flex flex-col justify-between p-4 sm:p-6 relative">
      {/* Top Navigation Bar with Logo, Dark/Light Mode & Language Switcher */}
      <header className="flex items-center justify-between max-w-5xl mx-auto w-full z-10 py-2">
        <Link href="/" className="flex items-center space-x-3 group">
          <div className="size-9 rounded-xl bg-gradient-to-br from-primary to-secondary shadow-md shadow-primary/20 transition-transform group-hover:scale-105" />
          <span className="text-xl font-extrabold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent tracking-tight">
            Career Loop
          </span>
        </Link>

        <div className="flex items-center gap-2">
          <ModeToggle />
          <LanguageSwitcher />
        </div>
      </header>

      {/* Main Login Card - High Visibility with Rounded-3xl Card Radius */}
      <main className="flex-1 flex items-center justify-center my-auto py-8 z-10">
        <div className="w-full max-w-md bg-card/95 dark:bg-slate-900/90 rounded-3xl shadow-2xl dark:shadow-xl dark:shadow-primary/10 border border-border/80 dark:border-slate-700/80 ring-1 ring-border/50 dark:ring-slate-700/60 p-6 sm:p-9 backdrop-blur-xl transition-all">
          {/* Header */}
          <div className="text-center mb-8 space-y-2">
            <h1 className="text-3xl font-extrabold tracking-tight bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              {t("title")}
            </h1>
            <p className="text-sm text-muted-foreground font-medium">
              {t("subtitle")}
            </p>
          </div>

          {/* Form */}
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-semibold text-foreground mb-2">
                {t("phoneLabel")}
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground font-semibold text-sm border-r border-border pr-2.5">
                  +251
                </span>
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={handlePhoneChange}
                  placeholder={t("phonePlaceholder")}
                  disabled={isCodeSent}
                  className="w-full pl-22 pr-4 py-3.5 rounded-2xl border border-input bg-background text-foreground text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-xs"
                />
              </div>
            </div>

            {isCodeSent && (
              <div className="space-y-2 animate-in fade-in slide-in-from-top-2 duration-300">
                <label className="block text-sm font-semibold text-foreground mb-1">
                  {t("codePlaceholder")}
                </label>
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="000000"
                  className="w-full px-4 py-3.5 border border-input rounded-2xl bg-background text-foreground text-center font-mono text-xl font-bold tracking-[0.4em] focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all shadow-xs"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && code.length === 6) handleVerify();
                  }}
                />
              </div>
            )}

            {!isCodeSent ? (
              <Button
                onClick={handleGetCode}
                disabled={isLoading || !phoneNumber}
                className="w-full h-12 bg-gradient-to-r from-primary to-secondary text-primary-foreground font-bold rounded-2xl shadow-lg shadow-primary/20 hover:shadow-primary/30 transition-all disabled:opacity-50 hover:cursor-pointer text-sm"
                size="lg"
              >
                {isLoading ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
                {isLoading ? "Sending Code..." : t("getCode")}
              </Button>
            ) : (
              <div className="space-y-3">
                <Button
                  onClick={handleVerify}
                  disabled={isLoading || !code}
                  className="w-full h-12 bg-gradient-to-r from-primary to-secondary text-primary-foreground font-bold rounded-2xl shadow-lg shadow-primary/20 hover:shadow-primary/30 transition-all disabled:opacity-50 hover:cursor-pointer text-sm"
                  size="lg"
                >
                  {isLoading ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
                  {isLoading ? "Verifying..." : "Verify & Login"}
                </Button>
                <Button
                  onClick={handleGetCode}
                  disabled={isLoading || waitTime > 0}
                  variant="outline"
                  className="w-full h-10 border border-input font-semibold rounded-xl hover:bg-muted transition-all disabled:opacity-50 hover:cursor-pointer text-xs"
                  size="sm"
                >
                  {waitTime > 0 ? t("waitTime", { seconds: waitTime }) : t("resendCode")}
                </Button>
              </div>
            )}
          </div>

          {/* Ethio Telecom Subscription Section with Rounded-2xl Rectangle */}
          <div className="mt-8 pt-6 border-t border-border">
            <div className="bg-muted/40 dark:bg-slate-800/60 border border-border/80 dark:border-slate-700/60 rounded-2xl p-5 text-center space-y-3 shadow-xs">
              <a
                href="sms:8526?body=ok"
                className="block bg-gradient-to-r from-primary to-secondary text-primary-foreground font-bold py-3 px-6 rounded-xl shadow-md hover:shadow-lg transition-all hover:cursor-pointer text-sm"
              >
                {t("subscribeButton")}
              </a>
              <p className="text-xs text-foreground/90 font-medium leading-relaxed">{t("subscriptionInfo")}</p>
              <div className="space-y-1 text-[11px] text-muted-foreground leading-normal">
                <p>{t("trial")}</p>
                <p>{t("unsubscribe")}</p>
                <p>{t("support")}</p>
              </div>
            </div>
          </div>

          {/* Footer Links */}
          <div className="mt-6 text-center text-xs text-muted-foreground space-x-2">
            <Link href="/tos" className="hover:text-foreground transition-colors">
              Terms and Conditions
            </Link>
            <span>•</span>
            <Link href="/faq" className="hover:text-foreground transition-colors">
              FAQ
            </Link>
          </div>
        </div>
      </main>

      {/* Page Footer */}
      <footer className="text-center text-xs text-muted-foreground py-2 z-10">
        © {new Date().getFullYear()} Career Loop Ethiopia. All rights reserved.
      </footer>
    </div>
  );
}
